import { buildCommandGroups } from "./command";
import {
    renderDecorators,
    shouldRenderDecorators,
} from "./compositeDecorators";
import { EntityState } from "./entityState";
import { StyleState } from "./styleState";
import { Node, appendChild, fragment, text } from "./tree";
import { Block, Config, ContentState, EntityMap } from "./types";
import { WrapperState } from "./wrapperState";

/**
 * Render a single block to a tree node.
 * Returns null if the block was added to an existing wrapper.
 */
function renderBlock<ConfigType extends Config>(
    block: Block<string, string>,
    entityMap: EntityMap<string>,
    wrapperState: WrapperState,
    config: ConfigType,
): Node | null {
    const blockText = block.text;
    const hasStyles = Boolean(block.inlineStyleRanges.length);
    const hasEntities = Boolean(block.entityRanges.length);
    const hasDecorators = shouldRenderDecorators(
        config.compositeDecorators,
        blockText,
    );

    let content: Node;

    // Complex path: styles and/or entities present
    if (hasStyles || hasEntities) {
        const contentNode = fragment();
        const entityState = new EntityState(config.entityDecorators, entityMap);
        const styleState = hasStyles ? new StyleState(config.styleMap) : null;

        // Process command groups
        for (const [textSlice, commands] of buildCommandGroups(block)) {
            // Apply commands to state machines
            for (const command of commands) {
                entityState.apply(command);
                if (styleState) {
                    styleState.apply(command);
                }
            }

            // Step 1: Apply decorators (but not inside entities)
            let decoratedNode: Node;
            if (hasDecorators && entityState.hasNoEntity()) {
                decoratedNode = renderDecorators(
                    config.compositeDecorators,
                    textSlice,
                    block,
                    wrapperState.blocks,
                );
            } else {
                decoratedNode = text(textSlice);
            }

            // Step 2: Apply inline styles
            const styledNode = styleState
                ? styleState.renderStyles(
                      decoratedNode,
                      block,
                      wrapperState.blocks,
                  )
                : decoratedNode;

            // Step 3: Apply entities (may buffer or wrap)
            const entityNode = entityState.renderEntities(
                styledNode,
                block,
                wrapperState.blocks,
            );

            // Append if we got a node back (null means it was buffered)
            if (entityNode !== null) {
                appendChild(contentNode, entityNode);

                // If entity created a different node, and we're not in an entity,
                // we may need to append the styled content separately
                if (styledNode !== entityNode && entityState.hasNoEntity()) {
                    appendChild(contentNode, styledNode);
                }
            }
        }

        content = contentNode;
    }
    // Fast path: only decorators
    else if (hasDecorators) {
        content = renderDecorators(
            config.compositeDecorators,
            blockText,
            block,
            wrapperState.blocks,
        );
    }
    // Fastest path: plain text
    else {
        content = text(blockText);
    }

    // Wrap with block element (handles list nesting)
    // Returns null if element was added to an existing wrapper
    return wrapperState.elementFor(block, content);
}

/**
 * Render the full content state to a tree.
 */
export function render<ConfigType extends Config>(
    contentState: ContentState<ConfigType>,
    config: ConfigType,
): Node {
    const { blocks, entityMap } = contentState;
    const wrapperState = new WrapperState(config.blockMap, blocks);
    const document = fragment();

    let minDepth = Infinity;

    for (const block of blocks) {
        const depth = ("depth" in block ? block.depth : undefined) ?? 0;
        const elt = renderBlock(block, entityMap, wrapperState, config);

        if (depth < minDepth) {
            minDepth = depth;
        }

        // Append element if we got one back
        // (null means it was added to an existing wrapper)
        if (elt !== null) {
            appendChild(document, elt);
        }
    }

    // If all blocks were nested (minDepth > 0), get the root wrapper
    if (minDepth > 0) {
        const root = wrapperState.getRootIfNested();
        if (root) {
            return root;
        }
    }

    return document;
}
